// This code has been partially generated by an AI.
import { computed, inject, Service, signal } from '@angular/core';
import { distinctUntilChanged, first, forkJoin, map, Observable, switchMap, tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { localStorageSignal } from '@ddtmm/angular-signal-generators';
import { EmployeeDto, StatusGroup } from '@app/_api/dto';
import { UUID } from '@app/_api/identifyable';
import { EmployeeApi } from '@app/core/services/employee.api';
import { RoleEnum } from '@app/_api/enums/role.enum';
import { AdminApi } from '@app/core/services/admin.api';

@Service()
export class EmployeeDataService {
  employee = signal<EmployeeDto | undefined>(undefined);
  tags = signal<string[] | undefined>(undefined);
  allStatusGroups = signal<StatusGroup[] | undefined>(undefined);
  hasChangedSinceLastConfirmed = signal<boolean>(false);
  readonly hasLocalPicture = signal(false);

  isAdmin = computed(() => this.hasRole(this.employee(), RoleEnum.ADMIN));
  isPersonal = computed(() => this.hasRole(this.employee(), RoleEnum.PERSONAL));
  isReviewer = computed(() => this.hasRole(this.employee(), RoleEnum.REVIEWER));
  isAdminOrReviewer = computed(() => this.hasRole(this.employee(), RoleEnum.ADMIN, RoleEnum.REVIEWER));

  private readonly employeeApi = inject(EmployeeApi);
  private readonly adminApi = inject(AdminApi);
  private readonly pictureVersions = localStorageSignal<Record<string, string>>({}, 'employeePictureVersions');
  private readonly pictureVersions$ = toObservable(this.pictureVersions);

  /**
   * Ensures the current employee exists in the SkillTree DB, then loads the data depending on it.
   * @returns the registered employee
   */
  register(): Observable<EmployeeDto> {
    return this.employeeApi.registerCurrentEmployee().pipe(
      tap(employee => this.employee.set(employee)),
      tap(() => this.initialize()),
    );
  }

  getAllEmployees(): Observable<EmployeeDto[]> {
    return this.employeeApi.getAllEmployees();
  }

  updateVisibilityGroups(visibilityGroups: StatusGroup[]) {
    return this.employeeApi.updateVisibilityGroups(visibilityGroups).pipe(
      first(),
      tap(updatedEmployee => {
        this.employee.set(updatedEmployee);
      }),
    );
  }

  getPictureForEmployee(uuid: string, quality: number = 0) {
    const imageResolution = this.getImageResolution(quality);
    return this.pictureVersions$.pipe(
      map(versions => versions[uuid] ?? '1'),
      distinctUntilChanged(),
      switchMap(version => this.employeeApi.getPictureForEmployee(uuid, imageResolution, version)),
      tap(picture => {
        if (uuid === this.employee()?.uuid) this.hasLocalPicture.set(picture.hasLocalPicture);
      }),
    );
  }

  uploadPicture(picture: Blob) {
    const uuid = this.employee()!.uuid;
    return this.employeeApi.uploadPicture(picture).pipe(tap(() => this.pictureChanged(uuid, true)));
  }

  deletePicture() {
    const uuid = this.employee()!.uuid;
    return this.employeeApi.deletePicture().pipe(tap(() => this.pictureChanged(uuid, false)));
  }

  private pictureChanged(uuid: UUID, hasLocalPicture: boolean) {
    this.hasLocalPicture.set(hasLocalPicture);
    this.pictureVersions.update(versions => ({ ...versions, [uuid]: crypto.randomUUID() }));
  }

  confirm() {
    this.hasChangedSinceLastConfirmed.set(false);
    this.employeeApi.confirm().subscribe(employee => {
      this.employee.set(employee);
    });
  }

  getImageResolution(quality: number) {
    return QUALITY[quality] || QUALITY[0];
  }

  updateEmployee(employee: EmployeeDto) {
    return this.adminApi.updateEmployee(employee);
  }

  getEmployeeById(employeeId: UUID) {
    return this.employeeApi.getEmployeeById(employeeId);
  }

  private hasRole(employee: EmployeeDto | undefined, ...role: RoleEnum[]) {
    const employeeRole = employee?.role;
    if (!employeeRole) return false;
    return role.includes(employeeRole);
  }

  private initialize() {
    forkJoin({
      allStatusGroups: this.employeeApi.getAllStatusGroups(),
      employeeConfirmation: this.employeeApi.areAllAssignmentsConfirmedForEmployee(),
    })
      .pipe(
        first(),
        tap(({ allStatusGroups, employeeConfirmation }) => {
          this.allStatusGroups.set(allStatusGroups);
          this.hasChangedSinceLastConfirmed.set(!employeeConfirmation.isConfirmed);
        }),
      )
      .subscribe();
  }
}

const QUALITY: Readonly<Record<number, string>> = {
  0: '48x48',
  1: '64x64',
  2: '96x96',
  3: '120x120',
  4: '240x240',
  5: '360x360',
  6: '432x432',
  7: '504x504',
  8: '648x648',
};
